import {hasScheduleErrors, scheduleWithOrderedBreaks} from './scheduleErrors'
import ConfirmDeleteButton from '../confirmation/ConfirmDeleteButton'
import React, {useContext, useState} from 'react'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import Typography from '@mui/material/Typography'
import {useNavigate} from 'react-router-dom'
import {ScheduleContext} from './ScheduleContext'
import {useSessionControls} from '../session/SessionContext'
import {useCloudFunctionsService} from '../backend/CloudFunctionsServiceContext'

function BlindsSchedulesRoute() {
  const {deleteSchedule: deleteSavedSchedule} = useCloudFunctionsService()
  const navigate = useNavigate()
  const {startSchedule, canControl} = useSessionControls()
  const {schedules, updateSchedules} = useContext(ScheduleContext)
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false)

  const [error, setError] = useState<string | null>(null)

  const startNewSchedule = async (scheduleName: string) => {
    const schedule = schedules.find(schedule => schedule.name === scheduleName)
    if (!schedule || hasScheduleErrors(schedule)) return
    setError(null)
    try {
      startSchedule(scheduleWithOrderedBreaks(schedule), () => { void Promise.resolve(navigate('/')).catch(() => setError('Unable to start schedule. Please try again.')) })
    } catch {
      setError('Unable to start schedule. Please try again.')
    }
  }

  const deleteSchedule = async (scheduleName: string) => {
    setIsLoadingSchedules(true)
    setError(null)
    try {
      await deleteSavedSchedule(scheduleName)
      await updateSchedules()
    } catch {
      setError('Unable to delete schedule. Please try again.')
    } finally {
      setIsLoadingSchedules(false)
    }
  }

  return (
    <>
      <Stack spacing={3} sx={{width: '100%', maxWidth: 1200, mx: 'auto'}}>
        <Box><Typography variant="h3" component="h1">Schedules</Typography>
          <Typography color="text.secondary" sx={{mt: 1}}>Choose a structure for your next tournament.</Typography></Box>
        {error && <Alert severity="error">{error}</Alert>}
        <Backdrop aria-label={'Loading'} open={isLoadingSchedules} sx={{zIndex: theme => theme.zIndex.drawer + 1}}><CircularProgress color="inherit"/></Backdrop>
        <Stack>
          <TableContainer component={Paper} >
            <Table aria-label="schedule table" sx={{'& th, & td': {px: {xs: 1.5, sm: 3}, py: 2.5}}}>
              <TableHead>
                <TableRow>
                  <TableCell>Schedule Name</TableCell>
                  <TableCell align={'right'}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!!schedules && schedules.map((row) => (
                  <TableRow
                    key={row.name}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row" sx={{width: {xs: '45%', sm: 'auto'}, overflowWrap: 'anywhere'}}><Typography sx={{fontWeight: 600}}>{row.name}</Typography></TableCell>
                    <TableCell align={'right'}>
                      <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} sx={{justifyContent: 'flex-end'}}>
                        <Button disabled={!canControl || hasScheduleErrors(row)} variant="outlined" onClick={() => startNewSchedule(row.name)}>Start</Button>
                        <Button variant="outlined" onClick={() => navigate(`/schedules/${row.name}`)}>View / Edit</Button>
                        <ConfirmDeleteButton label="Delete" subject={`schedule “${row.name}”`} description="This saved schedule will be permanently deleted." disabled={isLoadingSchedules} onConfirm={() => {void deleteSchedule(row.name)}}/>
                      </Stack>
                      {hasScheduleErrors(row) && <Typography color="error" variant="body2">This schedule has errors. View / Edit to correct them.</Typography>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
        {!schedules?.length && <Typography color="text.secondary">Add the default schedule to get started</Typography>}
      </Stack>
    </>
  )
}

export default BlindsSchedulesRoute
